import React, { useEffect, useState, useCallback, useMemo } from 'react';
import TableHeader from '../data-tables/TableHeader';
import TableToolbar from '../data-tables/TableToolbar';
import WellNotesDataTable from '../data-tables/WellNotesDataTable';
import AddWellNoteModal from '../add-wellnote-modal/AddWellNoteModal';
import { useAppSelector, useAppDispatch } from '../../hooks/storeHooks';
import {
  fetchPumpCheckerEvents,
  selectAllEvents,
  selectEventsStatus,
  selectEventsError,
  EventData,
} from '../slices/WellNoteSlice';
import * as XLSX from 'xlsx';
import Modal from '../../features/common/Modal/Modal';
import shutdownIcon from '../../images/shutdown.svg';
import { useToastService } from '../toast/ToastService';
import { usePermissionModal } from '../../features/common/PermissionModal/PermissionModalService';
import { useParams } from 'react-router-dom';
import { R, useRBAC, WIDGETS } from '../../rbac';
import { deleteWellNote } from '../../services/dashboard/AddWellNoteService';
import { fetchWells } from '../../services/dashboard/TaskDetailsService';
// import { showWarningToast } from '../toast/toastBridge';
import { formatDate } from '../../utilities/dateHelpers';

const WellNotesWidget = ({
  wellNotesRefreshTrigger,
  handleApplyClickedWellNotesWidget,
  wellId,
  dashboardName,
  isESP = true
}: {
  wellNotesRefreshTrigger: boolean;
  handleApplyClickedWellNotesWidget: (columns: any[], isConfigCheckBoxClicked: boolean) => void;
  wellId: number | undefined;
  dashboardName: string;
  isESP?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const resource = R.widget(dashboardName, WIDGETS.WELL_NOTES);
  const { can } = useRBAC();
  const rPermission = useMemo(
    () => ({
      canExport: can(resource, 'export'),
      canConfigureColumns: can(resource, 'configureColumns'),
      canAdd: can(resource, 'add'),
      canEdit: can(resource, 'edit'),
      canDelete: can(resource, 'delete'),
      canView: can(resource, 'view'),
    }),
    [can, resource],
  );

  // Debug permissions (can be removed in production)
  useEffect(() => {
    console.log('Well Notes permissions:', rPermission);
  }, [rPermission]);

  const wellTestData = useAppSelector((state) => state.WellTestIdSlice?.data);
  const wellNotesData = useAppSelector(selectAllEvents);
  const wellNotesStatus = useAppSelector(selectEventsStatus);
  const wellNotesError = useAppSelector(selectEventsError);

  const [isWellNotesTableInitialized, setIsWellNotesTableInitialized] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // committed columns actually rendered in the table (includes fixedColumns later)
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  // fixed (non-removable) columns
  const [fixedColumns, setFixedColumns] = useState<string[]>([]);
  // all column ids (excluding actions) used for ordering references
  const [allColumnsForToolbar, setAllColumnsForToolbar] = useState<string[]>([]);
  const [isAddWellNoteModalOpen, setIsAddWellNoteModalOpen] = useState(false);
  const [editingWellNote, setEditingWellNote] = useState<EventData | null>(null);
  const [selectedWellNoteToDelete, setDeleteWellNote] = useState<EventData | null>(null);
  const [isDeleteWellNoteModalOpen, setIsDeleteWellNoteModalOpen] = useState(false);
  const [wellName, setWellName] = useState<string>('');
  const [isConfigCheckBoxClicked, setsConfigCheckBoxClicked] = useState(false);
  const { showSuccessToast, showErrorToast } = useToastService();
  // Staged column configuration (order + visibility) edited via toolbar prior to Apply
  interface ColumnConfig {
    id: string;
    Header: string;
    isVisible: boolean;
  }
  interface WidgetColumnProp {
    key: string;
    label?: string;
    selected?: boolean;
    order?: number; // order coming from notesTask widget API
  }
  interface WellNotesWidgetProps {
    columns?: WidgetColumnProp[];
    fixedColumns?: WidgetColumnProp[];
  }
  interface TestWellColumnsSliceState {
    data?: {
      widgets?: {
        key: string;
        widgetProperty?: { widgetProperties?: WellNotesWidgetProps };
      }[];
    };
  }
  const [updatedColumns, setUpdatedColumns] = useState<ColumnConfig[]>([]);
  const [wellNotesWidgetProps, setWellNotesWidgetProps] = useState<WellNotesWidgetProps>({});
  const [processedWellNotesData, setProcessedWellNotesData] = useState<EventData[]>([]);

  // Memoize the callback to prevent continuous re-renders
  const handleFilteredSortedDataReady = useCallback((data: EventData[]) => {
    setProcessedWellNotesData(data);
  }, []);
  const { assetId } = useParams<{ assetId: string }>();
  const { showPermissionModal } = usePermissionModal();

  type ColumnDef = { Header: string; accessor?: string; id?: string };
  const columnDefs: ColumnDef[] = [
    { Header: '', id: 'actions' },
    { Header: 'Category', accessor: 'Category' },
    { Header: 'Comment', accessor: 'Comment' },
    { Header: 'Source', accessor: 'Source' },
    { Header: 'Created by', accessor: 'Username' },
    { Header: 'Created date', accessor: 'Created' },
  ];

  // Designed order for non-fixed visible columns. Adjust as needed.
  const [designedColumnOrder, setDesignedColumnOrder] = useState<string[]>(['Category', 'Comment', 'Source', 'Username', 'Created']);

  // Helper to sort strings based on a designed order array.
  // Items not present in the designed order are placed after the ordered ones, alphabetically.
  const sortByDesignedOrder = useCallback((arr: string[], order: string[]) => {
    const orderIndex = new Map(order.map((v, i) => [v, i]));
    return arr.slice().sort((a, b) => {
      const ai = orderIndex.has(a) ? (orderIndex.get(a) as number) : Number.MAX_SAFE_INTEGER;
      const bi = orderIndex.has(b) ? (orderIndex.get(b) as number) : Number.MAX_SAFE_INTEGER;
      if (ai !== bi) return ai - bi;
      return a.localeCompare(b);
    });
  }, []);

  const getColumnHeader = useCallback(
    (columnId: string) => {
      const col = columnDefs.find((col) => col.accessor === columnId || col.id === columnId);
      return col?.Header || columnId;
    },
    [],
  );

  const pumpCheckerPageData = useAppSelector(
    (state: { testWellColumnsSlice: TestWellColumnsSliceState }) => state.testWellColumnsSlice.data?.widgets,
  );
  const wellNotesWidget = pumpCheckerPageData?.find((obj) => obj.key === 'well-notes');

  useEffect(() => {
    const props: WellNotesWidgetProps = wellNotesWidget?.widgetProperty?.widgetProperties || {};
    setWellNotesWidgetProps(props);

    // Get all available column keys from columnDefs (excluding 'actions')
    const allAvailableColumnKeys = columnDefs
      .filter((col) => col.id !== 'actions' && (col.accessor || col.id))
      .map((col) => (col.accessor || col.id) as string);

    // Get API columns and merge with available columns to ensure all are present
    const apiColumns = props.columns || [];
    const apiColumnKeys = new Set(apiColumns.map((col) => col.key));

    // Add any missing columns from columnDefs to the API columns
    const mergedColumns: WidgetColumnProp[] = [...apiColumns];
    allAvailableColumnKeys.forEach((key) => {
      if (!apiColumnKeys.has(key)) {
        // Add missing column with default order (after API columns)
        mergedColumns.push({
          key,
          selected: true, // Not selected by default if not in API
          order: apiColumns.length + allAvailableColumnKeys.indexOf(key) + 1,
        });
      }
    });

    // Sort columns by provided 'order' (ascending); fallback to original sequence if order missing
    const sortedColumns = mergedColumns.slice().sort((a, b) => {
      const aOrder = typeof a.order === 'number' ? a.order : Number.MAX_SAFE_INTEGER;
      const bOrder = typeof b.order === 'number' ? b.order : Number.MAX_SAFE_INTEGER;
      return aOrder - bOrder;
    });

    let fixed = ['actions', ...(props.fixedColumns?.map((col) => col.key).filter(Boolean) || [])];
    // Remove 'Category' from fixed columns if present per new requirement
    if (fixed.includes('Category')) {
      fixed = fixed.filter((c) => c !== 'Category');
      console.debug('WellNotesWidget: removed "Category" from fixedColumns');
    }
    const visible = sortedColumns.filter((col) => col.selected).map((col) => col.key) || [];
    const all = sortedColumns.map((col) => col.key) || [];
    setFixedColumns(fixed);
    setVisibleColumns(visible);
    setAllColumnsForToolbar(all);

    // Initialize updatedColumns from existing configuration maintaining order
    const initialUpdatedColumns: ColumnConfig[] = sortedColumns.map((col) => ({
      id: col.key,
      Header: getColumnHeader(col.key),
      isVisible: visible.includes(col.key),
    }));
    setUpdatedColumns(initialUpdatedColumns);
    // }

    setIsWellNotesTableInitialized(true);
  }, [wellNotesWidget, assetId, getColumnHeader]);

  // Debug modal state changes
  useEffect(() => {
    console.log('Modal state changed:', { isAddWellNoteModalOpen, editingWellNote });
  }, [isAddWellNoteModalOpen, editingWellNote]);

  useEffect(() => {
    const fetchData = async () => {
      if (wellId || assetId) {
        dispatch(fetchPumpCheckerEvents({ wellId, assetId, isESP }));
        if (isESP) {
          setWellName(wellTestData?.Name || '');
        } else {
          const [wellsData] = await Promise.all([fetchWells(isESP)])
          if (wellsData && wellsData.length > 0) {
            setWellName(wellsData.find(item => item.Key.toString() === assetId?.toString())?.Value || '');
          }
        }
      }
    };

    fetchData();
  }, [wellId, assetId, wellNotesRefreshTrigger, isESP, dispatch]);

  // Receive reordered / visibility-toggled columns from toolbar (staged only)
  const handleUpdateColumns = (updated: ColumnConfig[]) => {
    setUpdatedColumns(updated);
  };

  // Toggle visibility in staged config only (does not affect rendered table until Apply)
  const handleColumnToggle = (columnId: string) => {
    setUpdatedColumns((prev) => prev.map((col) => (col.id === columnId ? { ...col, isVisible: !col.isVisible } : col)));
  };

  const resetHandler = () => {
    if (!wellNotesWidgetProps?.columns?.length) return;

    const resetAll = wellNotesWidgetProps.columns.map((col) => col.key);
    const staged: ColumnConfig[] = resetAll.map((id: string) => ({
      id,
      Header: getColumnHeader(id),
      isVisible: true,
    }));
    setAllColumnsForToolbar(resetAll);
    setUpdatedColumns(staged); // do not commit to visibleColumns yet
  };

  const handleSearch = (term: string) => setSearchTerm(term);

  const handleApplyClicked = () => {
    // Commit staged visibility/order to actual visibleColumns
    const committedVisible = updatedColumns.filter((c) => c.isVisible).map((c) => c.id);
    setVisibleColumns(committedVisible);
    const transformedArray = updatedColumns.map((item, index) => ({
      order: index + 1,
      label: item.Header,
      key: item.id,
      selected: item.isVisible,
    }));
    handleApplyClickedWellNotesWidget(transformedArray, isConfigCheckBoxClicked);
    // IMPORTANT: Use column IDs (keys) for ordering, not labels.
    // Labels like 'Created by' won't match IDs like 'Username', causing mis-sorts.
    setDesignedColumnOrder(transformedArray.map(t => t.key));
  };

  const handleSaveConfig = (config: boolean) => {
    setsConfigCheckBoxClicked(config);
  };

  const editWellNoteClickHandler = useCallback(
    (wellNote: EventData) => {
      if (!rPermission.canEdit) {
        console.log('No edit permission, showing permission modal');
        showPermissionModal('edit', 'well note');
        return;
      }

      console.log('Setting editing well note and opening modal');
      setEditingWellNote(wellNote);
      setIsAddWellNoteModalOpen(true);
    },
    [rPermission.canEdit, showPermissionModal],
  );

  const deleteWellNoteClickHandler = useCallback(
    (wellNote: EventData) => {
      if (!rPermission.canDelete) {
        showPermissionModal('delete', 'well note');
        return;
      }
      setDeleteWellNote(wellNote);
      setIsDeleteWellNoteModalOpen(true);
    },
    [rPermission.canDelete, showPermissionModal],
  );

  const onSaveColumns = () => {
    handleApplyClicked();
  };

  const handleExportXLS = () => {
    try {
      const exportData = processedWellNotesData.map((wellNote) => {
        const rowData: Record<string, string | number> = {};
        visibleColumns?.forEach((col) => {
          if (col !== 'actions') {
            const raw = (wellNote as unknown as { [k: string]: unknown })[col];
            let value: string | number = '';
            // Format the Created date column using standardized formatDate helper
            if (col === 'Created' && raw) {
              value = formatDate(raw as Date | string | number | null | undefined, false);
            } else if (typeof raw === 'string' || typeof raw === 'number') {
              value = raw;
            } else if (raw != null) {
              value = String(raw);
            }
            rowData[getColumnHeader(col)] = value ?? '';
          }
        });
        return rowData;
      });

      const today = new Date();
      const formattedDate = today.toISOString().split('T')[0].replace(/-/g, '');
      const fileName = `Well-Notes-${wellName}-${formattedDate}.xlsx`;

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error('Error exporting to XLSX:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedWellNoteToDelete || (isESP ? !wellId : !assetId)) return;

    try {
      const success = await deleteWellNote(selectedWellNoteToDelete.id, isESP);
      if (success) {
        showSuccessToast('Well comment deleted successfully');
        dispatch(fetchPumpCheckerEvents({ wellId, assetId, isESP }));
      } else {
        showErrorToast('Failed to delete well comment', 'There was an error deleting the well comment. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting well note:', error);
      showErrorToast('Failed to delete well note', 'There was an error deleting the well note. Please try again.');
    }

    setIsDeleteWellNoteModalOpen(false);
    setDeleteWellNote(null);
  };

  const addWellNoteHandler = () => {
    console.log('Adding new well note - clearing edit state');
    setEditingWellNote(null);
    setIsAddWellNoteModalOpen(true);
  };

  // Memoize the combined visible columns (committed columns only)
  const memoizedVisibleColumns = useMemo(() => {
    const orderedVisible = sortByDesignedOrder(visibleColumns, designedColumnOrder);
    return [...fixedColumns, ...orderedVisible];
  }, [fixedColumns, visibleColumns, sortByDesignedOrder, designedColumnOrder]);

  // Memoize the columns array for TableToolbar to prevent recreation
  const memoizedToolbarColumns = useMemo(
    () =>
      (updatedColumns.length
        ? updatedColumns
        : allColumnsForToolbar.map((id) => ({
          id,
          Header: getColumnHeader(id),
          isVisible: visibleColumns.includes(id),
        }))
      ).filter((col) => !fixedColumns.includes(col.id)),
    [updatedColumns, allColumnsForToolbar, fixedColumns, visibleColumns, getColumnHeader],
  );

  return (
    <main className='main-container table-wrap tasknotes-dashboard' style={{ zIndex: 'auto', minHeight: 'calc(96vh - 250px)' }}>
      <TableHeader
        title={'Comments'}
        buttonLabel='Add comment'
        onAddTestClick={addWellNoteHandler}
        rPermission={rPermission}
        tableName='wellNotes'
      />

      <TableToolbar
        tableName='wellNotes'
        disableAllInstallations={true}
        columns={memoizedToolbarColumns}
        onColumnToggle={handleColumnToggle}
        onSaveColumns={onSaveColumns}
        onSearch={handleSearch}
        onUpdateColumns={handleUpdateColumns}
        onApplyClicked={() => handleApplyClicked()}
        saveConfigHasbeenClicked={handleSaveConfig}
        onExportXLS={handleExportXLS}
        resetToDefaultSystemClicked={() => {
          resetHandler();
        }}
        isTaskTableInitialized={isWellNotesTableInitialized}
        ref={undefined}
        rPermission={rPermission}
      />

      {memoizedVisibleColumns.length > 0 && (
        <WellNotesDataTable
          visibleColumns={memoizedVisibleColumns}
          searchTerm={searchTerm}
          wellNotesData={wellNotesData}
          onEditWellNote={editWellNoteClickHandler}
          onDeleteWellNote={deleteWellNoteClickHandler}
          onUpdateVisibleColumns={setVisibleColumns}
          isConfigCheckBoxEnabled={isConfigCheckBoxClicked}
          onFilteredSortedDataReady={handleFilteredSortedDataReady}
          rPermission={rPermission}
          isLoading={wellNotesStatus === 'loading'}
          error={wellNotesError}
        />
      )}

      {isAddWellNoteModalOpen && (
        <AddWellNoteModal
          isOpen={isAddWellNoteModalOpen}
          onClose={() => {
            setIsAddWellNoteModalOpen(false);
            setEditingWellNote(null);
            wellId && dispatch(fetchPumpCheckerEvents({ wellId, assetId, isESP }))
          }}
          onNoteSaved={() => (wellId || assetId) && dispatch(fetchPumpCheckerEvents({ wellId, assetId, isESP }))}
          wellNoteToEdit={editingWellNote}
          currentWellId={wellId || assetId}
          isPC={isESP}
          assetId={assetId}
          currentWellName={wellName}
        />
      )}

      <Modal
        open={isDeleteWellNoteModalOpen}
        setOpen={setIsDeleteWellNoteModalOpen}
        message='Are you sure you want to delete this well comment?'
        buttonText='Yes'
        callbackOnConfirm={handleDeleteConfirm}
        title='Delete well comment'
        Icon={shutdownIcon}
        buttonColor='red'
      />
    </main>
  );
};

export default WellNotesWidget;
